/**
 * Admin Overdue Watch — items THIS faculty approved that are now past
 * their `return_due_date`. Each row exposes an "Extend Return Time"
 * action which opens a modal collecting a reason category + free text
 * and a new due date. Backend enforces:
 *   • Only original approver (or superadmin) can extend.
 *   • Cumulative extension ≤ 30 days beyond the ORIGINAL due date.
 *
 * All data is fetched from existing endpoints —
 *   GET  /api/orders/overdue-returnable?mine=true
 *   PUT  /api/orders/{orderId}/items/{componentId}/extend-return
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertOctagon, Clock, User, X } from 'lucide-react';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { shortRef } from '../ui';

const REASON_OPTIONS = [
  { value: 'treatment_extended',       label: 'Treatment time extended' },
  { value: 'patient_missed_appointment', label: 'Patient missed appointment' },
  { value: 'missing_components',       label: 'Patient reported missing components' },
  { value: 'other',                    label: 'Other (specify below)' },
];

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const addDays = (isoDate, days) => {
  const d = new Date(isoDate);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export default function OverduePage({ ctx }) {
  const { showToast } = ctx;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extend, setExtend] = useState(null); // { row, new_due, reason_category, reason_text }
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(endpoints.orders.overdueReturnableMine);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('overdue-mine', e);
      showToast && showToast('Failed to load your overdue items', 'err');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const openExtend = (row) => {
    const capRemaining = Math.max(0, (row.cap_days ?? 30) - (row.cumulative_days_extended ?? 0));
    const suggested = addDays(row.current_due_date, Math.min(7, capRemaining || 1));
    setExtend({
      row,
      new_due: suggested,
      reason_category: 'treatment_extended',
      reason_text: '',
      capRemaining,
    });
  };

  const submitExtend = async () => {
    if (!extend) return;
    const { row, new_due, reason_category, reason_text } = extend;
    if (!reason_text.trim()) {
      showToast && showToast('Reason text is required', 'err');
      return;
    }
    setSaving(true);
    try {
      await apiClient.put(
        endpoints.orders.extendReturnDue(row.order_id, row.component_id),
        { new_due_date: new_due, reason_category, reason_text: reason_text.trim() },
      );
      showToast && showToast('Return due date extended', 'ok');
      setExtend(null);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Extension failed';
      showToast && showToast(msg, 'err');
    } finally {
      setSaving(false);
    }
  };

  const totals = useMemo(() => ({
    items: rows.length,
    students: new Set(rows.map((r) => r.student_id)).size,
    extended: rows.filter((r) => (r.extension_count || 0) > 0).length,
  }), [rows]);

  return (
    <div data-testid="ap-overdue-page">
      <div className="ap-eyebrow">Faculty Console</div>
      <h1 className="ap-page-title">My Overdue Watch</h1>
      <p className="ap-page-lede">
        Items on the requests <strong>you approved</strong> that are past their return due date.
        You can extend the return timeline (up to +30&nbsp;days cumulative) with a documented reason.
      </p>

      <div className="ap-ind-row" role="group" aria-label="Overdue summary">
        <div className="ap-ind" data-testid="ap-overdue-count-items">
          <div className="ap-ind-label"><em className="is-exc" />Overdue Items</div>
          <div className="ap-ind-value">{totals.items}</div>
          <div className="ap-ind-hint">Approved by you, past return date</div>
        </div>
        <div className="ap-ind" data-testid="ap-overdue-count-students">
          <div className="ap-ind-label"><em className="is-exc" />Students Affected</div>
          <div className="ap-ind-value">{totals.students}</div>
          <div className="ap-ind-hint">Distinct students overdue</div>
        </div>
        <div className="ap-ind" data-testid="ap-overdue-count-extended">
          <div className="ap-ind-label"><em className="is-warn" />Already Extended</div>
          <div className="ap-ind-value">{totals.extended}</div>
          <div className="ap-ind-hint">Have at least one extension logged</div>
        </div>
      </div>

      <div className="ap-mt-6">
        {loading ? (
          <div className="ap-empty" data-testid="ap-overdue-loading">Loading your overdue items…</div>
        ) : rows.length === 0 ? (
          <div className="ap-empty" data-testid="ap-overdue-empty">
            <div style={{ fontSize: 28, marginBottom: 8 }}><Clock size={26} /></div>
            <p><strong>Nothing overdue.</strong></p>
            <p className="ap-subtle">Items you approved are all within their return window.</p>
          </div>
        ) : (
          <div className="ap-table-wrap" data-testid="ap-overdue-table">
            <table className="ap-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Patient · PID</th>
                  <th>Component</th>
                  <th style={{ textAlign: 'right' }}>Qty</th>
                  <th>Original Due</th>
                  <th>Current Due</th>
                  <th style={{ textAlign: 'right' }}>Days Over</th>
                  <th>Extensions</th>
                  <th style={{ textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const capRemaining = Math.max(0, (r.cap_days ?? 30) - (r.cumulative_days_extended ?? 0));
                  return (
                    <tr key={`${r.order_id}-${r.component_id}`} data-testid={`ap-overdue-row-${r.order_id}-${r.component_id}`}>
                      <td>
                        <div style={{ fontWeight: 500, color: 'var(--diis-ink)' }}>{r.student_name || '—'}</div>
                        <div className="ap-subtle">{shortRef(r.order_id, 'REQ')}</div>
                      </td>
                      <td>
                        <div>{r.patient_name || '—'}</div>
                        <div className="ap-subtle">{r.patient_pid || '—'}</div>
                      </td>
                      <td>
                        <div>{r.component_name || '—'}</div>
                        <div className="ap-subtle">{r.brand_name || '—'}{r.size ? ` · ${r.size}` : ''}</div>
                      </td>
                      <td style={{ textAlign: 'right' }} className="cp-mono-num">{r.quantity}</td>
                      <td>{fmtDate(r.original_due_date)}</td>
                      <td>{fmtDate(r.current_due_date)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <span style={{ color: '#B04A3C', fontWeight: 600 }}>{r.days_overdue}</span>
                      </td>
                      <td>
                        {(r.extension_count || 0) === 0 ? (
                          <span className="ap-subtle">None</span>
                        ) : (
                          <div>
                            <div>{r.extension_count}× (+{r.cumulative_days_extended || 0}d)</div>
                            <div className="ap-subtle" title={r.latest_extension_reason_text || ''} style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {(r.latest_extension_reason_category || '').replace(/_/g, ' ')}
                            </div>
                          </div>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className="ap-btn ap-btn-primary"
                          onClick={() => openExtend(r)}
                          disabled={capRemaining <= 0}
                          title={capRemaining <= 0 ? '30-day cumulative cap reached' : `Up to +${capRemaining} days remaining`}
                          data-testid={`ap-overdue-extend-btn-${r.order_id}-${r.component_id}`}
                        >
                          Extend
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {extend && (
        <ExtendModal
          extend={extend}
          setExtend={setExtend}
          onClose={() => setExtend(null)}
          onSubmit={submitExtend}
          saving={saving}
        />
      )}
    </div>
  );
}

function ExtendModal({ extend, setExtend, onClose, onSubmit, saving }) {
  const { row, new_due, reason_category, reason_text, capRemaining } = extend;
  const origIso = (row.original_due_date || row.current_due_date || '').slice(0, 10);
  const maxIso = addDays(origIso, row.cap_days ?? 30);
  const curIso = (row.current_due_date || '').slice(0, 10);
  const projected = new Date(new_due).getTime() - new Date(origIso).getTime();
  const projectedDays = Math.max(0, Math.round(projected / 86400000));

  return (
    <div className="ap-modal-scrim" role="dialog" aria-modal="true" data-testid="ap-overdue-extend-modal" onClick={onClose}>
      <div className="ap-modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="ap-modal-header">
          <div>
            <h3 style={{ margin: 0, fontFamily: 'var(--diis-font-display)', fontWeight: 500, fontSize: 20 }}>Extend return due date</h3>
            <p className="ap-subtle" style={{ margin: '4px 0 0', fontSize: 12 }}>
              {row.component_name} · {row.quantity} unit(s) · patient {row.patient_name || '—'} ({row.patient_pid || '—'})
            </p>
          </div>
          <button type="button" className="ap-btn ap-btn-ghost ap-btn-sm" onClick={onClose} data-testid="ap-overdue-extend-close" aria-label="Close">
            <X size={14} />
          </button>
        </div>

        <div className="ap-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div className="ap-field-label">Current due date</div>
              <div>{fmtDate(row.current_due_date)}</div>
            </div>
            <div>
              <div className="ap-field-label">Original due date</div>
              <div>{fmtDate(row.original_due_date)}</div>
            </div>
          </div>

          <label>
            <div className="ap-field-label">New due date</div>
            <input
              type="date"
              className="ap-input"
              value={new_due}
              min={curIso}
              max={maxIso}
              onChange={(e) => setExtend({ ...extend, new_due: e.target.value })}
              data-testid="ap-overdue-extend-new-due"
            />
            <div className="ap-subtle" style={{ fontSize: 12, marginTop: 4 }}>
              Cumulative from original: <strong>+{projectedDays}d</strong> / {row.cap_days ?? 30}d cap
              {capRemaining <= 0 && <span style={{ color: '#B04A3C' }}> · cap reached</span>}
            </div>
          </label>

          <label>
            <div className="ap-field-label">Reason category</div>
            <select
              className="ap-input"
              value={reason_category}
              onChange={(e) => setExtend({ ...extend, reason_category: e.target.value })}
              data-testid="ap-overdue-extend-reason-cat"
            >
              {REASON_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </label>

          <label>
            <div className="ap-field-label">Details <span className="ap-subtle">(required, will be logged)</span></div>
            <textarea
              className="ap-textarea"
              rows={3}
              value={reason_text}
              onChange={(e) => setExtend({ ...extend, reason_text: e.target.value })}
              placeholder="Brief clinical justification for extension (audit trail)."
              data-testid="ap-overdue-extend-reason-text"
            />
          </label>
        </div>

        <div className="ap-modal-footer">
          <button type="button" className="ap-btn ap-btn-ghost" onClick={onClose} data-testid="ap-overdue-extend-cancel">Cancel</button>
          <button
            type="button"
            className="ap-btn ap-btn-primary"
            onClick={onSubmit}
            disabled={saving || !reason_text.trim() || projectedDays === 0 || projectedDays > (row.cap_days ?? 30)}
            data-testid="ap-overdue-extend-submit"
          >
            {saving ? 'Saving…' : 'Confirm extension'}
          </button>
        </div>
      </div>
    </div>
  );
}
